import React from "react";
import { Link } from "react-router-dom";
import AgencyMark from "./AgencyMark";
import { PhoneCall, MessageSquare, MapPin, Shield, ExternalLink } from "lucide-react";

export default function PublicFooter() {
  return (
    <footer
      data-testid="public-footer"
      className="relative overflow-hidden"
      style={{ background: "linear-gradient(170deg, #0b2b5e 0%, #061830 50%, #020d1f 100%)" }}
    >
      {/* Background decorations */}
      <div className="absolute inset-0 bg-dots opacity-15" aria-hidden />
      <div
        className="absolute -top-24 right-0 h-80 w-80 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(250,204,21,0.08) 0%, transparent 70%)" }}
        aria-hidden
      />
      <div
        className="absolute bottom-0 left-0 h-60 w-60 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)" }}
        aria-hidden
      />

      {/* Top accent line */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: "linear-gradient(90deg, transparent, rgba(250,204,21,0.5) 30%, rgba(59,130,246,0.5) 70%, transparent)" }}
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <div className="grid md:grid-cols-3 gap-12">

          {/* Brand column */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="p-1 rounded-xl" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}>
                <AgencyMark className="h-14 w-14 shrink-0" />
              </div>

              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-slate-500 font-semibold">
                  Ditresnarkoba
                </div>
                <div className="font-display font-extrabold text-white">
                  Polda Jawa Tengah
                </div>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-slate-400 max-w-xs">
              Portal pelaporan resmi Direktorat Reserse Narkoba Polda Jawa Tengah.
              Identitas pelapor dilindungi berdasarkan peraturan perundang-undangan yang berlaku.
            </p>

            {/* Shield badge */}
            <div className="mt-5 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold text-emerald-400"
              style={{ background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.2)" }}>
              <Shield className="h-3.5 w-3.5" />
              Sistem Terproteksi
            </div>
          </div>

          {/* Emergency contacts */}
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-yellow-400 mb-5">
              Kontak Darurat
            </div>
            <ul className="space-y-3">
              {[
                { icon: PhoneCall, label: "Call Center Polri", val: "110", href: "tel:110", color: "text-yellow-400" },
                { icon: PhoneCall, label: "Polda Jateng", val: "(024) 8443441", href: "tel:+62248443441", color: "text-blue-400" },
                { icon: MessageSquare, label: "SMS Pengaduan", val: "1717", href: "sms:1717", color: "text-cyan-400" },
              ].map((c) => (
                <li key={c.label}>
                  <a
                    href={c.href}
                    className="group flex items-center justify-between gap-4 p-3 rounded-xl transition-all hover:scale-[1.02]"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <c.icon className={`h-4 w-4 ${c.color} flex-shrink-0`} />
                      <span className="text-sm text-slate-400">{c.label}</span>
                    </div>
                    <span className={`font-mono-tight font-bold text-white group-hover:${c.color} transition-colors text-sm`}>
                      {c.val}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Address + links */}
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-yellow-400 mb-5">
              Informasi
            </div>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Jl.%20Pahlawan%20No.1%2C%20Kota%20Semarang%2C%20Jawa%20Tengah%2050249%2C%20Indonesia"
              target="_blank"
              rel="noreferrer"
              className="group flex items-start gap-3 p-4 rounded-xl mb-4 transition-all hover:scale-[1.02]"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <MapPin className="h-4 w-4 text-rose-400 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-xs text-slate-500 mb-1">Alamat Kantor</div>
                <div className="text-sm text-slate-300 leading-relaxed group-hover:text-white transition-colors">
                  Jl. Pahlawan No.1, Kota Semarang,<br />
                  Jawa Tengah 50249, Indonesia
                </div>
                <div className="flex items-center gap-1 mt-2 text-xs text-blue-400">
                  <ExternalLink className="h-3 w-3" />
                  Lihat di Maps
                </div>
              </div>
            </a>

            <div className="space-y-2">
              {[
                { to: "/", label: "Beranda" },
                { to: "/lapor", label: "Buat Laporan" },
                { to: "/lacak", label: "Lacak Status Laporan" },
                { to: "/admin/login", label: "Portal Petugas" },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="flex items-center gap-2 text-sm text-slate-400 hover:text-yellow-400 transition-colors"
                >
                  <span className="h-px w-4 bg-current opacity-50" />
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className="mt-12 pt-6 flex flex-col sm:flex-row justify-between gap-2 text-xs text-slate-600"
          style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}
        >
          <div>© {new Date().getFullYear()} Ditresnarkoba Polda Jateng. Semua hak dilindungi.</div>
          <div className="flex items-center gap-1">
            <AgencyMark type="polri" className="h-5 w-5" />
            Portal Resmi — Kepolisian Negara Republik Indonesia
          </div>
        </div>
      </div>
    </footer>
  );
}
